import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

export interface MultiSelectOption {
    id: number;
    name: string;
    data?: any;
}

export interface MultiSelectConfig {
    placeholder?: string;
    min?: number;
    max: number;
    editable?: boolean;
}

@Component({
    selector: 'ng-multiselect',
    templateUrl: './multi-select.widget.reports.component.html',
    host: {
        class: 'cmps-reports-multi-select',
    },
})
export class MultiSelectComponent implements OnInit, OnDestroy {
    @Input() config: MultiSelectConfig;

    // eslint-disable-next-line accessor-pairs
    @Input() set options(value: MultiSelectOption[]) {
        console.log('setting options...', [...value]);
        this._options = value;
        this.setInitials();
    }

    @Input() value: MultiSelectOption[] = [];
    @Output() change = new EventEmitter<MultiSelectOption[]>();
    _options: MultiSelectOption[] = [];
    filtered: MultiSelectOption[] = [];
    availables: MultiSelectOption[] = [];

    search: FormControl;
    sub: Subscription;

    constructor(private readonly _fb: FormBuilder) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnInit() {
        this.search = this._fb.control(null);
        this.sub = this.search.valueChanges
            .pipe(
                tap((value: string) => {
                    this.filtered = this.availables
                        .filter((a) =>
                            a.name.toLowerCase().includes(value.toLowerCase()),
                        )
                        .sort(this.onSort);
                }),
            )
            .subscribe();
        this.setInitials();
    }

    /** @private */
    _onMoveUpClick(opt: MultiSelectOption): void {
        const index = this.value.indexOf(opt);
        if (index <= 0) {
            return;
        }

        this.value.splice(index - 1, 0, this.value.splice(index, 1)[0]);
        this.change.next(this.value);
    }

    /** @private */
    _onMoveDownClick(opt: MultiSelectOption): void {
        const index = this.value.indexOf(opt);
        if (index >= this.value.length - 1) {
            return;
        }

        this.value.splice(index + 1, 0, this.value.splice(index, 1)[0]);
        this.change.next(this.value);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private setInitials() {
        this.availables = [
            ...this._options.filter(
                (o) => !this.value.some((v) => v.id === o.id),
            ),
        ];
        this.filtered = [...this.availables].sort(this.onSort);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onClickAddItem(metricId: number) {
        if (this.value.length === this.config.max) {
            return;
        }

        const available = this.filtered.findIndex((o) => o.id === metricId);
        if (available > -1) {
            this.value.push(...this.filtered.splice(available, 1));
        }

        const optionB = this.availables.findIndex((o) => o.id === metricId);
        if (optionB > -1) {
            this.availables.splice(optionB, 1);
        }

        this.change.next(this.value);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onClickRemoveItem(metricId: number) {
        const deleted = this.value.splice(
            this.value.findIndex((o) => o.id === metricId),
            1,
        );
        this.filtered.push(...deleted);
        this.filtered = [...this.filtered.sort(this.onSort)];
        this.availables.push(...deleted);
        this.change.next(this.value);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    onClickEdit(opt: MultiSelectOption, el: HTMLInputElement) {
        opt.data.isEditing = !opt.data.isEditing;
        if (opt.data.isEditing) {
            setTimeout(() => {
                el.focus();
            }, 1);
        }
    }

    onKeyPress(event: any): boolean {
        const charCode = event.which ? event.which : event.keyCode;

        if (charCode === 46) {
            return true;
        }

        if (charCode > 31 && (charCode < 48 || charCode > 57)) {
            return false;
        }
        return true;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    private onSort(a: MultiSelectOption, b: MultiSelectOption) {
        return a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }
}
